import { markRaw } from 'vue'
import HireTenureColumn from './HireTenureColumn.vue'
import HireClassification from './HireClassification.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import {
  HireAvatarColumnFragment,
  HireAvatarColumnFragmentDoc,
  HireEngagementColumnFragment,
  HireEngagementColumnFragmentDoc,
  HireCreatedColumnFragment,
  HireCreatedColumnFragmentDoc,
  HireOrderByColumn,
  SortOrder,
  HireContractPopoverColumnFragment,
  HireContractPopoverColumnFragmentDoc,
  RateColumnHireFragmentDoc,
  RateColumnHireFragment,
  HireHasStaffingAgencyColumnFragmentDoc,
  HireHasStaffingAgencyColumnFragment,
  HirePaymentRequestColumnFragmentDoc,
  HirePaymentRequestColumnFragment,
  HireLastPaymentRequestColumnFragmentDoc,
  HireLastPaymentRequestColumnFragment,
  HireTitleNavigationPoNumberColumnFragmentDoc,
  HireTitleNavigationPoNumberColumnFragment,
  HireTitleNavigationColumnFragmentDoc,
  HireTitleNavigationColumnFragment,
  HireActiveStatusColumnFragmentDoc,
  HireActiveStatusColumnFragment,
  HireCompanyColumnFragmentDoc,
  HireCompanyColumnFragment,
  HireTitleCompanyColumnFragmentDoc,
  HireTitleCompanyColumnFragment,
  HireTenureColumnFragmentDoc,
  HireTenureColumnFragment,
  HireClassificationColumnFragment,
  HireClassificationColumnFragmentDoc,
  HireWorkerColumnFragmentDoc,
  HireWorkerColumnFragment,
} from '@/gql/graphql'
import HireAvatarColumn from '@/components/GraphqlDataTable/columns/Hire/HireAvatarColumn.vue'
import HireWorkerColumn from '@/components/GraphqlDataTable/columns/Hire/HireWorkerColumn.vue'
import HireEngagement from '@/components/GraphqlDataTable/columns/Hire/HireEngagement.vue'
import HireCreatedColumn from '@/components/GraphqlDataTable/columns/Hire/HireCreatedColumn.vue'
import HireContractPopoverColumn from '@/components/GraphqlDataTable/columns/Hire/HireContractPopoverColumn.vue'
import RateColumn from '@/components/GraphqlDataTable/columns/RateColumn.vue'
import HireHasStaffingAgencyColumn from '@/components/GraphqlDataTable/columns/Hire/HireHasStaffingAgencyColumn.vue'
import HirePaymentRequestColumn from '@/components/GraphqlDataTable/columns/Hire/HirePaymentRequestColumn.vue'
import HireLastPaymentRequestColumn from '@/components/GraphqlDataTable/columns/Hire/HireLastPaymentRequestColumn.vue'
import HireTitleNavigationPoNumberColumn from '@/components/GraphqlDataTable/columns/Hire/HireTitleNavigationPoNumberColumn.vue'
import HireTitleNavigationColumn from '@/components/GraphqlDataTable/columns/Hire/HireTitleNavigationColumn.vue'
import HireActiveStatusColumn from '@/components/GraphqlDataTable/columns/Hire/HireActiveStatusColumn.vue'
import HireCompanyColumn from '@/components/GraphqlDataTable/columns/Hire/HireCompanyColumn.vue'
import HireTitleCompanyColumn from '@/components/GraphqlDataTable/columns/Hire/HireTitleCompanyColumn.vue'
import { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'

// Read lazily via getters: account globals are absent on logged-out pages, so actsAsSupplier throws at module scope there.
const { actsAsSupplier } = useAccountCapabilities()

const columns: ColumnMaps = {
  HireAvatarColumn: {
    fragment: HireAvatarColumnFragmentDoc,
    component: markRaw(HireAvatarColumn),
    columnDef: {
      title: $t('Worker'),
      key: 'HireAvatarColumnFragmentDoc',
      orderAble: true,
      renderUsing: {
        component: markRaw(HireAvatarColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true, // We have a custom click event so we want that to fire instead of row select
    },
    dataDescriber: [
      {
        title: 'Worker Id',
        extraction: (row: HireAvatarColumnFragment) => `#${(row.worker as any).id}`,
      },
      {
        title: 'Worker name',
        extraction: (row: HireAvatarColumnFragment) => (row.worker as any).name,
      },
      {
        title: 'Worker email',
        extraction: (row: HireAvatarColumnFragment) => row.worker.email || '',
      },
      {
        title: 'Status',
        extraction: (row: HireAvatarColumnFragment) => `${row.activeStatus}`,
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.WorkerName}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.WorkerName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Worker name'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the worker name'),
      },
      {
        name: `${HireOrderByColumn.WorkerName}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.WorkerName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Worker name'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the worker name'),
      },
    ],
  },
  HireEngagementColumn: {
    fragment: HireEngagementColumnFragmentDoc,
    component: markRaw(HireEngagement),
    columnDef: {
      title: $t('Engagement type'),
      key: 'engagementType',
      orderAble: true,
      renderUsing: {
        component: markRaw(HireEngagement),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Engagement Type',
        extraction: (row: HireEngagementColumnFragment) => `${row.engagementTypeLabel}`,
      },
      {
        title: 'Engagement Type Setup',
        extraction: (row: HireEngagementColumnFragment) => `${row.engagementTypeSetupLabel}`,
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.EngagementType}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.EngagementType,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Engagement type'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the engagement type'),
      },
      {
        name: `${HireOrderByColumn.EngagementType}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.EngagementType,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Engagement type'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the engagement type'),
      },
    ],
  },
  HireClassificationColumn: {
    fragment: HireClassificationColumnFragmentDoc,
    component: markRaw(HireClassification),
    columnDef: {
      title: $t('Classify'),
      key: 'classification',
      renderUsing: {
        component: markRaw(HireClassification),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Classification',
        extraction: (row: HireClassificationColumnFragment) =>
          row.usesClassification
            ? row.classification?.overridesAnother
              ? 'Classification overridden'
              : 'Using Worksome Classify'
            : '-',
      },
    ],
  },
  HireCreatedColumn: {
    fragment: HireCreatedColumnFragmentDoc,
    component: markRaw(HireCreatedColumn),
    columnDef: {
      title: $t('Created'),
      key: 'createdAt',
      orderAble: true,
      renderUsing: {
        component: markRaw(HireCreatedColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Created',
        extraction: (row: HireCreatedColumnFragment) => row.createdAt,
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.CreatedAt}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.CreatedAt,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Created'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the created date'),
      },
      {
        name: `${HireOrderByColumn.CreatedAt}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.CreatedAt,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Created'),
        secondaryText: $t('New to old'),
        title: $t('Order by the created date'),
      },
    ],
  },
  HireTitleNavigationPoNumberColumn: {
    fragment: HireTitleNavigationPoNumberColumnFragmentDoc,
    component: markRaw(HireTitleNavigationPoNumberColumn),
    columnDef: {
      title: $t('Hire Name | PO number'),
      key: 'job.id',
      orderAble: true,
      renderUsing: {
        component: markRaw(HireTitleNavigationPoNumberColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true, // We have a custom click event so we want that to fire instead of row select
    },
    dataDescriber: [
      {
        title: 'Job title',
        extraction: (row: HireTitleNavigationPoNumberColumnFragment) => row.job.name ?? '',
      },
      {
        title: 'Job url',
        extraction: (row: HireTitleNavigationPoNumberColumnFragment) =>
          `${window.location.origin}${laravelNamedRoute(namedRoutes.jobs.show, { job: row.job.id })}`,
      },
      {
        title: 'Hire url',
        extraction: (row: HireTitleNavigationPoNumberColumnFragment) =>
          `${window.location.origin}${laravelNamedRoute(namedRoutes.hires.hire.view, { hire: row?.id, tab: 'overview' })}`,
      },
      {
        title: 'Purchase order',
        extraction: (row: HireTitleNavigationPoNumberColumnFragment) =>
          row.purchaseOrderNumber ?? '-',
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.JobName}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.JobName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Hire'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the hire title'),
      },
      {
        name: `${HireOrderByColumn.JobName}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.JobName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Hire'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the hire title'),
      },
    ],
  },
  HireTitleNavigationColumn: {
    fragment: HireTitleNavigationColumnFragmentDoc,
    component: markRaw(HireTitleNavigationColumn),
    columnDef: {
      title: $t('Contract'),
      key: 'job.id',
      colClass: { 'tw-max-w-[400px]': true },
      orderAble: true,
      renderUsing: {
        component: markRaw(HireTitleNavigationColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true,
    },
    dataDescriber: [
      {
        title: 'Job title',
        extraction: (row: HireTitleNavigationColumnFragment) => row.job.name ?? '',
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.JobName}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.JobName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Job'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the job name'),
      },
      {
        name: `${HireOrderByColumn.JobName}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.JobName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Job'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the job name'),
      },
    ],
  },
  HireTitleCompanyColumn: {
    fragment: HireTitleCompanyColumnFragmentDoc,
    component: markRaw(HireTitleCompanyColumn),
    columnDef: {
      get title() {
        return actsAsSupplier.value ? $t('Role | Client') : $t('Role | Company')
      },
      key: 'hire_title_company',
      colClass: { 'tw-max-w-[400px]': true },
      orderAble: true,
      renderUsing: {
        component: markRaw(HireTitleCompanyColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true,
    },
    dataDescriber: [
      {
        title: 'Hire job title',
        extraction: (row: HireTitleCompanyColumnFragment) => row.latestContract.jobName ?? '',
      },
      {
        get title() {
          return actsAsSupplier.value ? 'Client' : 'Company'
        },
        extraction: (row: HireTitleCompanyColumnFragment) =>
          actsAsSupplier.value
            ? (row.company?.name ?? row.latestContract.companyName ?? '')
            : (row.latestContract.companyName ?? row.company?.name ?? ''),
      },
      {
        title: 'Hire url',
        extraction: (row: HireTitleCompanyColumnFragment) =>
          `${window.location.origin}${laravelNamedRoute(namedRoutes.hires.hire.view, { hire: row?.id, tab: 'overview' })}`,
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.JobName}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.JobName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Hire title'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the hire title'),
      },
      {
        name: `${HireOrderByColumn.JobName}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.JobName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Hire title'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the hire title'),
      },
    ],
  },
  HireActiveStatusColumn: {
    fragment: HireActiveStatusColumnFragmentDoc,
    component: markRaw(HireActiveStatusColumn),
    columnDef: {
      title: $t('Status'),
      key: 'activeStatus',
      colClass: { 'tw-max-w-fit tw-w-1': true },
      renderUsing: {
        component: markRaw(HireActiveStatusColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Status',
        extraction: (row: HireActiveStatusColumnFragment) => `${row.activeStatus}`,
      },
    ],
  },
  HireCompanyColumn: {
    fragment: HireCompanyColumnFragmentDoc,
    component: markRaw(HireCompanyColumn),
    columnDef: {
      title: $t('Company'),
      key: 'company.id',
      orderAble: true,
      renderUsing: {
        component: markRaw(HireCompanyColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Company name',
        extraction: (row: HireCompanyColumnFragment) => row.company.name ?? '',
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.CompanyName}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.CompanyName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Company'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the company name'),
      },
      {
        name: `${HireOrderByColumn.CompanyName}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.CompanyName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Company'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the company name'),
      },
    ],
  },
  HireContractPopoverColumn: {
    fragment: HireContractPopoverColumnFragmentDoc,
    component: markRaw(HireContractPopoverColumn),
    columnDef: {
      title: $t('Contract'),
      key: 'HireContractPopoverColumn',
      renderUsing: {
        component: markRaw(HireContractPopoverColumn),
        componentProps: (row) => ({ row }),
      },
      ignoreClickForRowSelection: true, // We have a custom click event so we want that to fire instead of row select
    },
    dataDescriber: [
      {
        title: 'Contract',
        extraction: (row: HireContractPopoverColumnFragment) => `#${row.id}`,
      },
    ],
  },
  HirePaymentRequestColumn: {
    fragment: HirePaymentRequestColumnFragmentDoc,
    component: markRaw(HirePaymentRequestColumn),
    columnDef: {
      title: $t('Payment request'),
      key: 'HirePaymentRequestColumn',
      renderUsing: {
        component: markRaw(HirePaymentRequestColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Payment Requests',
        extraction: (row: HirePaymentRequestColumnFragment) => `#${row.id}`,
      },
    ],
  },
  HireLastPaymentRequestColumn: {
    fragment: HireLastPaymentRequestColumnFragmentDoc,
    component: markRaw(HireLastPaymentRequestColumn),
    columnDef: {
      title: $t('Last payment request'),
      key: 'lastPaymentRequestDate',
      orderAble: true,
      renderUsing: {
        component: markRaw(HireLastPaymentRequestColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Last payment request',
        extraction: (row: HireLastPaymentRequestColumnFragment) =>
          row.lastPaymentRequestDate ?? '-',
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.LastPaymentRequestDate}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.LastPaymentRequestDate,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Last payment request'),
        secondaryText: $t('Old to new'),
        title: $t('Order by the last payment request date'),
      },
      {
        name: `${HireOrderByColumn.LastPaymentRequestDate}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.LastPaymentRequestDate,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Last payment request'),
        secondaryText: $t('New to old'),
        title: $t('Order by the last payment request date'),
      },
    ],
  },
  HireHasStaffingAgencyColumn: {
    fragment: HireHasStaffingAgencyColumnFragmentDoc,
    component: markRaw(RateColumn),
    columnDef: {
      title: $t('Staffing Agency'),
      key: 'HireHasStaffingAgencyColumnFragmentDoc',
      renderUsing: {
        component: markRaw(HireHasStaffingAgencyColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Staffing agency',
        extraction: (row: HireHasStaffingAgencyColumnFragment) =>
          `${!row.recruiterManagesWorkers ? '-' : row.recruiterOwnershipIsExpired ? 'Expired' : 'Active'}`,
      },
    ],
  },
  RateColumnHire: {
    fragment: RateColumnHireFragmentDoc,
    component: markRaw(RateColumn),
    columnDef: {
      title: $t('Rate'),
      key: 'rate',
      renderUsing: {
        component: markRaw(RateColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Rate',
        extraction: (row: RateColumnHireFragment) => `${row.rate}`,
      },
      {
        title: 'Rate type',
        extraction: (row: RateColumnHireFragment) => `${row.rateType}`,
      },
      {
        title: 'Currency',
        extraction: (row: RateColumnHireFragment) => `${row.currency}`,
      },
      {
        title: 'Has milestones',
        extraction: (row: RateColumnHireFragment) => `${row.hasMilestones}`,
      },
    ],
  },
  HireTenureColumn: {
    fragment: HireTenureColumnFragmentDoc,
    component: markRaw(HireTenureColumn),
    columnDef: {
      title: $t('Active tenure'),
      key: 'tenure',
      flexClass: { 'tw-float-right': true },
      renderUsing: {
        component: markRaw(HireTenureColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Active tenure',
        extraction: (row: HireTenureColumnFragment) => `${row.tenure}`,
      },
    ],
  },
  HireWorkerColumn: {
    fragment: HireWorkerColumnFragmentDoc,
    component: markRaw(HireWorkerColumn),
    columnDef: {
      title: $t('Worker'),
      key: 'HireWorkerColumnFragmentDoc',
      orderAble: true,
      renderUsing: {
        component: markRaw(HireWorkerColumn),
        componentProps: (row) => ({ row }),
      },
    },
    dataDescriber: [
      {
        title: 'Worker name',
        extraction: (row: HireWorkerColumnFragment) => row.worker.name,
      },
    ],
    sort: [
      {
        name: `${HireOrderByColumn.WorkerName}_${SortOrder.Asc}`,
        orderByColumn: HireOrderByColumn.WorkerName,
        sortOrder: SortOrder.Asc,
        primaryText: $t('Worker name'),
        secondaryText: $t('A - Z'),
        title: $t('Order by the worker name'),
      },
      {
        name: `${HireOrderByColumn.WorkerName}_${SortOrder.Desc}`,
        orderByColumn: HireOrderByColumn.WorkerName,
        sortOrder: SortOrder.Desc,
        primaryText: $t('Worker name'),
        secondaryText: $t('Z - A'),
        title: $t('Order by the worker name'),
      },
    ],
  },
}
export default columns
